import React, { ReactNode, useState } from "react";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow, { TableRowProps } from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import MoreHorizIcon from "../../icons/components/more-horiz-icon";
import { makeStyles } from "@material-ui/styles";
import { Collapse, IconButton } from "@material-ui/core";
import { ChevronRightIcon } from "../../icons";
import { Color } from "../../theme";
import cns from "classnames";

type Props = {
  defaultCollapsedState?: boolean;
  headers: { width: string; content: ReactNode }[];
  childHeaders?: ReactNode[];
  rows:
    | (ReactNode | (() => void))[][]
    | {
        cells: (ReactNode | (() => void))[];
        children: ReactNode[];
        editProofs: ReactNode[];
      }[];
};

const Row = (props: {
  row: Props["rows"][0];
  collapsed?: boolean;
  childHeaders?: ReactNode[];
  headerLength: number;
  tableRowProps?: TableRowProps;
}) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  if ("children" in props.row && props.row.children?.length > 0) {
    return (
      <>
        <TableRow {...props.tableRowProps} className={classes.row}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </TableCell>
          {props.row.cells.map((cell, i) =>
            typeof cell === "function" ? (
              <TableCell className={classes.cursorPointer} key={i} onClick={cell}>
                <MoreHorizIcon />
              </TableCell>
            ) : (
              <TableCell key={i}>{cell}</TableCell>
            )
          )}
        </TableRow>
        <TableRow className={classes.row}>
          <TableCell style={{ padding: 0 }} colSpan={props.headerLength + 2}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <MuiTable>
                <TableBody>
                  {props.row.editProofs?.map((proof, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={props.headerLength + 1}>{proof}</TableCell>
                    </TableRow>
                  ))}
                  {props.row.children}
                </TableBody>
              </MuiTable>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  const cells = "cells" in props.row ? props.row.cells : props.row;

  return (
    <>
      <TableRow className={classes.row}>
        {"editProofs" in props.row && props.row.editProofs.length > 0 ? (
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </TableCell>
        ) : (
          <TableCell />
        )}
        {cells.map((cell, i) =>
          typeof cell === "function" ? (
            <TableCell key={i} onClick={cell} className={classes.cursorPointer}>
              <MoreHorizIcon />
            </TableCell>
          ) : (
            <TableCell key={i}>{cell}</TableCell>
          )
        )}
      </TableRow>
      {"editProofs" in props.row && props.row.editProofs.length > 0 && (
        <TableRow>
          <TableCell style={{ padding: 0 }} colSpan={props.headerLength + 2}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <MuiTable>
                <TableBody>
                  {props.row.editProofs.map((proof, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={props.headerLength + 1}>{proof}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export function Table(props: Props) {
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState(props.defaultCollapsedState ?? false);

  return (
    <TableContainer component={Paper} className={classes.noBoxShadow}>
      <MuiTable className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "5%" }} />
            {props.headers.map((header, i) => (
              <TableCell
                style={{
                  width: header.width
                }}
                key={i}
              >
                {header.content}
              </TableCell>
            ))}
            <TableCell className={classes.textAlignRight} style={{ width: "10%" }}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setCollapsed(!collapsed)}
              >
                {!collapsed ? (
                  <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={cns(collapsed ? classes.collapsable : "")}>
          {props.rows.map((row, i) => (
            <Row
              collapsed={collapsed}
              key={i}
              row={row}
              childHeaders={props.childHeaders}
              headerLength={props.headers.length}
            />
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}

const useStyles = makeStyles(() => ({
  noBoxShadow: {
    boxShadow: "none !important",
    overflowX: "visible"
  },
  cursorPointer: {
    cursor: "pointer",
    textAlign: "right"
  },
  textAlignRight: {
    textAlign: "right"
  },
  twoRowsOnly: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
    maxWidth: "17.5rem"
  },
  noBorder: {
    border: "none !important"
  },
  childHeaderCell: {
    border: "none",
    fontWeight: "bold",
    paddingBottom: "0 !important"
  },
  editInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: 0,
    border: "none",
    color: Color.DetailsInput
  },
  width100: {
    width: "100%"
  },
  collapsable: {
    display: "none"
  },
  root: {
    tableLayout: "fixed",
    "& th": {
      width: "auto"
    }
  },
  row: {
    position: "relative"
  }
}));
